import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowDownIcon, CheckIcon, ChevronRightIcon, FileTextIcon } from 'lucide-react';
import type { Partner } from '../../../types/partners';
import { PartnerLogo } from '../PartnerLogo';
import { PartnerImage } from '../PartnerImage';
import { UpvoteButton } from '../UpvoteButton';
import { TryPlatformButton } from '../TryPlatformButton';
export const PARTNER_BUTTON_CLASS = 'bg-[color:var(--pa)] hover:bg-[color:var(--pa-strong)] active:bg-[color:var(--pa-strong)]';
export function PartnerProfileHero({
  partner
}: {
  partner: Partner;
}) {
  const reduceMotion = useReducedMotion();
  const {
    hero
  } = partner.profile;
  const fade = (delay: number) => reduceMotion ? {} : {
    initial: {
      opacity: 0,
      y: 14
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.55,
      delay,
      ease: [0.22, 1, 0.36, 1] as [number, number, number, number]
    }
  };
  return <section id="partner-hero" aria-labelledby="partner-hero-heading" className="relative overflow-hidden border-b border-hairline-soft" data-id="element-639" data-mp="2bzg84xvybb7e">
      <div className="sf-grid-texture sf-fade-mask pointer-events-none absolute inset-0" aria-hidden="true" data-id="element-640" data-mp="2tcvrqzzmycyl" />
      <div className="relative mx-auto w-full max-w-[1320px] px-4 pb-16 pt-8 sm:px-6 lg:px-8 lg:pb-20 lg:pt-10" data-id="element-641" data-mp="2oczmkxf7cfhg">
        <nav aria-label="Breadcrumb" data-id="element-642" data-mp="2jefxwp984ple">
          <ol className="flex items-center gap-1.5 text-xs text-text-tertiary" data-id="element-643" data-mp="2efuwimi8jzvd">
            <li data-id="element-644" data-mp="2ehz9rv20yb8b"><Link to="/" className="hover:text-text-primary" data-id="element-645" data-mp="2ehzbbzhoajaf">Home</Link></li>
            <li aria-hidden="true" data-id="element-646" data-mp="29nlcfn3sxu10"><ChevronRightIcon className="h-3 w-3" data-id="element-647" data-mp="29n1sy1yi6xkv" /></li>
            <li data-id="element-648" data-mp="25om7i2i2lx9k"><Link to="/partners" className="hover:text-text-primary" data-id="element-649" data-mp="25om5xy2f9p7g">Partners</Link></li>
            <li aria-hidden="true" data-id="element-650" data-mp="20p9e0ywp1ry0"><ChevronRightIcon className="h-3 w-3" data-id="element-651" data-mp="20q17a32tjdl4" /></li>
            <li aria-current="page" className="text-text-secondary" data-id="element-652" data-mp="1vpwpm9q6cctb">{partner.name}</li>
          </ol>
        </nav>

        <div className="mt-10 grid gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:items-center lg:gap-14" data-id="element-653" data-mp="1qq0lmahh8ldr">
          <div className="flex flex-col items-start" data-id="element-654" data-mp="1h1woadm1f1yg">
            <motion.div {...fade(0)} className="flex h-12 items-center" data-id="element-655" data-mp="1m1rfyjdmzvkk">
              <PartnerLogo partner={partner} eager className="h-10 sm:h-11" data-id="element-656" data-mp="1lzoen371ag0j" />
            </motion.div>
            <motion.p {...fade(0.04)} className="mt-7 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.09em] text-[color:var(--pa)]" data-id="element-657" data-mp="1842w9evb2uh3">
              <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-[color:var(--pa-hl)]" data-id="element-658" data-mp="1zvieyxtqrx21" />
              {hero.eyebrow}
            </motion.p>
            <motion.h1 {...fade(0.08)} id="partner-hero-heading" className="mt-4 text-balance text-[38px] font-semibold leading-[1.02] tracking-[-0.04em] text-text-primary sm:text-[52px] lg:text-[58px]" data-id="element-659" data-mp="1qzt077msu0xm">
              {hero.headline}
            </motion.h1>
            <motion.p {...fade(0.12)} className="mt-5 max-w-xl text-base leading-relaxed text-text-secondary sm:text-lg" data-id="element-660" data-mp="2nkbe135nul0v">
              {hero.subheadline}
            </motion.p>
            {hero.statement ? <motion.p {...fade(0.14)} className="mt-4 border-l-2 border-[color:var(--pa-hl)] pl-3 text-[15px] font-medium text-text-primary" data-id="element-661" data-mp="74er14ln6jxi">
                {hero.statement}
              </motion.p> : null}

            <motion.div {...fade(0.18)} className="mt-8 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center" data-id="element-662" data-mp="gwuak6cl7gki">
              <TryPlatformButton partner={partner} placement="profile_hero" label={partner.tryLabel} size="lg" className={PARTNER_BUTTON_CLASS} data-id="element-663" data-mp="3kc01m3yc8j9g" />
              <UpvoteButton partner={partner} placement="profile_hero" variant="inline" data-id="element-664" data-mp="310928e57thf" />
              {partner.profile.reports ? <a href="#reports" className="inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-lg px-3 text-sm font-semibold text-[color:var(--pa)] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime" data-id="element-665" data-mp="1ei03panyqkem">
                  Explore Sample Reports
                  <ArrowDownIcon className="h-4 w-4" aria-hidden="true" data-id="element-666" data-mp="3k6smnvauux7u" />
                </a> : null}
            </motion.div>

            <motion.ul {...fade(0.22)} className="mt-8 flex flex-col gap-2" aria-label={`${partner.name} at a glance`} data-id="element-667" data-mp="3t6ywn77chf29">
              {partner.benefits.map(benefit => <li key={benefit} className="flex items-center gap-2 text-sm text-text-secondary" data-id="element-668" data-mp="1ytbfyyw0q7w">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[color:var(--pa-soft)] text-[color:var(--pa)]" data-id="element-669" data-mp="6xwbf500g690">
                    <CheckIcon className="h-3 w-3" strokeWidth={2.5} aria-hidden="true" data-id="element-670" data-mp="fskdkmaah7tl" />
                  </span>
                  {benefit}
                </li>)}
            </motion.ul>
          </div>

          {hero.featuredVisual ? <motion.figure {...reduceMotion ? {} : {
          initial: {
            opacity: 0,
            y: 20
          },
          animate: {
            opacity: 1,
            y: 0
          },
          transition: {
            duration: 0.7,
            delay: 0.15
          }
        }} className="mx-auto w-full min-w-0 max-w-[440px]" data-id="element-671" data-mp="poe110pjqlq5">
              <div className="overflow-hidden rounded-2xl border border-hairline bg-[color:var(--pa-soft)] p-3 shadow-lg sm:p-4" data-id="element-672" data-mp="pxajt4cq6y3m">
                <PartnerImage asset={hero.featuredVisual} eager className="h-auto w-full rounded-xl bg-panel" fallback={<div className="flex aspect-[16/10] w-full flex-col items-center justify-center gap-3 rounded-xl bg-panel text-center" data-id="element-674" data-mp="1dmlnoupdh8lz">
                      <FileTextIcon className="h-8 w-8 text-[color:var(--pa)]" aria-hidden="true" data-id="element-675" data-mp="14trsfntj5ymh" />
                      <p className="text-sm font-semibold text-text-primary" data-id="element-676" data-mp="21b604gfd9cwf">{partner.name} report cover</p>
                    </div>} data-id="element-673" data-mp="lwgtk4bt977k" />
              </div>
              <figcaption className="mt-3 text-center text-xs text-text-tertiary" data-id="element-677" data-mp="3qsjmrp17113z">Example landscape report structure, supplied by {partner.name}.</figcaption>
            </motion.figure> : null}
        </div>
      </div>
    </section>;
}